import FormattedMessage from 'I18n/components/FormattedMessage';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Link } from 'foundations-components/transitional/components/Link';
import { List as ImmutableList } from 'immutable';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SEARCH } from 'reporting-data/constants/configTypes';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { isFailed, isLoading, isSucceeded } from 'reporting-data/asyncData/AsyncData';
// @ts-expect-error ts-migrate(7016)
import { matchCurrencyProcessor } from 'reporting-data/process';
// @ts-expect-error ts-migrate(7016)
import { AMOUNT_IN_HOME_CURRENCY_PROPERTIES } from 'reporting-data/properties/constants/multiCurrencyProperties';
import { getConfigType, getMetricProperties, getReportProcessors } from 'reporting-data/report/configReportGetters';
import { isCustomWidgetReport, isMultiConfigReport } from 'reporting-data/tsTypes/reportTypes';
import { DateActionTypes } from 'reporting-ui-components/constants/trackingConstants';
import { EVENTS } from 'reporting-ui-components/lib/events';
import useCloneTracker from 'reporting-ui-components/lib/useCloneTracker';
// @ts-expect-error ts-migrate(7016)
import CurrencySelect from 'ui-addon-i18n/components/CurrencySelect';
import { ReportOptionsCategories } from '../../singleReportViewer/fullPage/sidebar/categorized/ReportOptionCategory';
import UpdateCurrenciesModal from './UpdateCurrenciesModal';
import { CURRENCY_CODE_PREFIX, MANAGE_CURRENCIES_URL } from './constants';
import { useFetchMultiCurrencyInfo } from './useFetchMultiCurrency';
import { FilterEditorWrapper } from '../Filters/FilterEditorWrapper';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MultiCurrencyEditor = ({
  appUsageTracker,
  isV2FilterExperience,
  onChange,
  options,
  report,
  onFailureToLoad,
  trackingApplicationName,
  trackingAssetType,
  trackingScreenName
}) => {
  const [showUpdateCurrenciesModal, setShowUpdateCurrenciesModal] = useState(false);
  const readOnly = options.readOnly;
  const currencyInfoFetch = useFetchMultiCurrencyInfo();
  const selectedCurrencyProcessor = getReportProcessors(report).find(processor => matchCurrencyProcessor(processor));
  const selectedCurrencyCode = selectedCurrencyProcessor ? selectedCurrencyProcessor.replace(CURRENCY_CODE_PREFIX, '') : null;
  const usageTracker = useCloneTracker(appUsageTracker);
  const handleOnChange = useCallback(({
    target: {
      value
    }
  }) => {
    usageTracker.track(EVENTS.changeSrvDateFilters, {
      action: DateActionTypes.CURRENCY,
      assetType: trackingAssetType || 'report',
      applicationName: trackingApplicationName,
      whereScreen: trackingScreenName,
      isNewFilterExperience: isV2FilterExperience
    });
    let newReportConfig = report.updateIn(['config', 'processors'], ImmutableList(), processors => processors.filter(processor => !matchCurrencyProcessor(processor)).concat(`${CURRENCY_CODE_PREFIX}${value}`));
    if (isMultiConfigReport(report)) {
      newReportConfig = newReportConfig.updateIn(['reportConfigs', 'secondary', 'config', 'processors'], ImmutableList(), processors => processors.filter(processor => !matchCurrencyProcessor(processor)).concat(`${CURRENCY_CODE_PREFIX}${value}`));
    }
    onChange(newReportConfig);
  }, [isV2FilterExperience, onChange, report, trackingApplicationName, trackingAssetType, trackingScreenName, usageTracker]);
  const currencyInfo = isSucceeded(currencyInfoFetch) ? currencyInfoFetch.data : undefined;
  const validCurrencies = useMemo(() => {
    return currencyInfo && currencyInfo.hasMulticurrencyActive && currencyInfo.currencyExchangeRates ? currencyInfo.currencyExchangeRates.map(rate => rate.fromCurrencyCode) : [];
  }, [currencyInfo]);
  const cannotEnableCurrencyEditor = !currencyInfo || !currencyInfo.hasMulticurrencyActive || !currencyInfo.currencyExchangeRates;
  const loading = isLoading(currencyInfoFetch);
  const failed = isFailed(currencyInfoFetch);
  useEffect(() => {
    if ((cannotEnableCurrencyEditor || failed) && !loading) {
      onFailureToLoad === null || onFailureToLoad === void 0 || onFailureToLoad('MultiCurrencyEditor');
    }
  }, [cannotEnableCurrencyEditor, loading, failed, onFailureToLoad]);
  if (isLoading(currencyInfoFetch) || cannotEnableCurrencyEditor || !isSucceeded(currencyInfoFetch)) {
    return null;
  }
  const dropdownFooter = /*#__PURE__*/_jsx(Link, {
    "data-test-id": "manage-currencies-link",
    external: true,
    href: MANAGE_CURRENCIES_URL,
    onClick: () => setShowUpdateCurrenciesModal(true),
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "settingLabels.multiCurrency.manageCurrencies"
    })
  });
  const label = () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "settingLabels.multiCurrency.label"
  });
  if (isV2FilterExperience) {
    const tooltipText = selectedCurrencyCode ? /*#__PURE__*/_jsx("div", {
      children: selectedCurrencyCode
    }) : null;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(FilterEditorWrapper, {
        isSelected: !!selectedCurrencyCode,
        children: /*#__PURE__*/_jsx(Tooltip, {
          title: tooltipText,
          children: /*#__PURE__*/_jsx(CurrencySelect, {
            anchorType: "button",
            ButtonContent: label,
            buttonUse: "transparent",
            menuWidth: 300,
            buttonSize: 'small',
            "data-test-id": "multi-currency-editor",
            dropdownFooter: dropdownFooter,
            multiCurrencyInfo: currencyInfo,
            readOnly: readOnly,
            validCurrencies: validCurrencies,
            value: selectedCurrencyCode,
            onChange: handleOnChange
          })
        })
      }), showUpdateCurrenciesModal && /*#__PURE__*/_jsx(UpdateCurrenciesModal, {
        handleOnClose: () => setShowUpdateCurrenciesModal(false)
      })]
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FormControl, {
      label: label(),
      children: /*#__PURE__*/_jsx(CurrencySelect, {
        "data-test-id": "multi-currency-editor",
        dropdownFooter: dropdownFooter,
        multiCurrencyInfo: currencyInfo,
        readOnly: readOnly,
        validCurrencies: validCurrencies,
        value: selectedCurrencyCode,
        onChange: handleOnChange
      })
    }), showUpdateCurrenciesModal && /*#__PURE__*/_jsx(UpdateCurrenciesModal, {
      handleOnClose: () => setShowUpdateCurrenciesModal(false)
    })]
  });
};
MultiCurrencyEditor.isUsableForReport = report => {
  const configType = getConfigType(report);
  const reportMetrics = getMetricProperties(report);
  const hasHomeCurrencyProperties = reportMetrics.some(metric => AMOUNT_IN_HOME_CURRENCY_PROPERTIES.includes(metric));
  const isCustomWidget = isCustomWidgetReport(report);
  return configType !== SEARCH && hasHomeCurrencyProperties && !isCustomWidget;
};
MultiCurrencyEditor.category = ReportOptionsCategories.More;
export default MultiCurrencyEditor;